import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { BarChart3, Cookie, LockKeyhole, PlaySquare, Settings2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { useConsent } from './ConsentContext'

export function CookieConsentManager() {
  const {
    consent,
    preferencesOpen,
    openPreferences,
    closePreferences,
    acceptAll,
    rejectOptional,
    savePreferences,
  } = useConsent()
  const [analytics, setAnalytics] = useState(false)
  const [media, setMedia] = useState(false)

  useEffect(() => {
    if (!preferencesOpen) return
    setAnalytics(consent?.analytics ?? false)
    setMedia(consent?.media ?? false)
  }, [consent?.analytics, consent?.media, preferencesOpen])

  return (
    <>
      {!consent && (
        <section
          role="dialog"
          aria-label="Cookie choices"
          aria-describedby="cookie-consent-description"
          className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-5xl overflow-hidden rounded-2xl border bg-background/95 shadow-2xl backdrop-blur-xl sm:inset-x-6 sm:bottom-6"
        >
          <div className="h-1 bg-linear-to-r from-brand-orange via-brand-yellow to-brand-purple" />
          <div className="grid gap-5 p-5 sm:p-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
            <div className="flex gap-4">
              <div className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-purple-light text-brand-black sm:flex dark:bg-brand-purple-dim dark:text-brand-purple">
                <Cookie className="h-5 w-5" aria-hidden="true" />
              </div>
              <div>
                <p className="text-base font-semibold">Your privacy, your choice</p>
                <p id="cookie-consent-description" className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground">
                  We use necessary technology to keep the site secure and make checkout work. With
                  your permission, we also use analytics to improve the site and load videos from
                  YouTube. You can change your choice at any time in the footer.
                </p>
                <div className="mt-2 text-xs">
                  <a href="/privacy" className="font-medium underline underline-offset-4 hover:text-foreground">
                    Privacy & cookie details
                  </a>
                </div>
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-3 lg:w-[27rem]">
              <Button type="button" variant="outline" onClick={openPreferences}>
                <Settings2 aria-hidden="true" />
                Customize
              </Button>
              <Button type="button" className="bg-brand-blue text-brand-black hover:bg-brand-blue/85" onClick={rejectOptional}>
                Reject optional
              </Button>
              <Button type="button" className="bg-brand-orange text-brand-black hover:bg-brand-orange/85" onClick={acceptAll}>
                Accept all
              </Button>
            </div>
          </div>
        </section>
      )}

      <Dialog open={preferencesOpen} onOpenChange={(open) => !open && closePreferences()}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Cookie className="h-5 w-5 text-brand-purple" aria-hidden="true" />
              Cookie preferences
            </DialogTitle>
            <DialogDescription className="leading-6">
              Choose which optional technologies One Day Dance may use. Necessary technology is
              always active because the site cannot securely operate without it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <PreferenceRow
              icon={<LockKeyhole aria-hidden="true" />}
              title="Necessary"
              description="Security, consent memory, sign-in, cart, and payment fraud prevention when you enter checkout."
              checked
              disabled
              onCheckedChange={() => undefined}
            />
            <PreferenceRow
              icon={<BarChart3 aria-hidden="true" />}
              title="Analytics & performance"
              description="Firebase and Google Analytics help us understand visits, errors, and page performance."
              checked={analytics}
              onCheckedChange={setAnalytics}
            />
            <PreferenceRow
              icon={<PlaySquare aria-hidden="true" />}
              title="External media"
              description="Allows privacy-enhanced YouTube players to load on film pages."
              checked={media}
              onCheckedChange={setMedia}
            />
          </div>

          <p className="text-xs leading-5 text-muted-foreground">
            Turning analytics off after it has loaded refreshes the page so collection stops and
            One Day Dance analytics identifiers can be removed from this browser.
          </p>

          <DialogFooter className="sm:justify-between">
            <Button type="button" variant="outline" onClick={rejectOptional}>
              Reject optional
            </Button>
            <Button type="button" onClick={() => savePreferences({ analytics, media })}>
              Save preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function PreferenceRow({
  icon,
  title,
  description,
  checked,
  disabled = false,
  onCheckedChange,
}: {
  icon: ReactNode
  title: string
  description: string
  checked: boolean
  disabled?: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  const id = `cookie-preference-${title.toLowerCase().replaceAll(/[^a-z]+/g, '-')}`

  return (
    <div className="flex items-start gap-3 rounded-xl border bg-card/60 p-4">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground [&_svg]:h-4 [&_svg]:w-4">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="font-medium">{title}</label>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">{description}</p>
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        aria-label={`${title} cookies`}
        onCheckedChange={onCheckedChange}
      />
    </div>
  )
}
